import Link from 'next/link';
import React, { FC } from 'react';
import { IoBagHandleOutline, IoGlobeOutline } from 'react-icons/io5';

import { Anchor, Group, Text } from '@mantine/core';

import { CompanyProfile, PublishedStatus } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { BadgePill } from 'components/badges/BadgePill';
import { useProfilesByIds } from 'hooks/use-profiles-by-ids';

import { useComponentContext } from '../../hooks/use-component-context';
import { BadgeGroup } from './BadgeGroup';

const GeneralView = () => {
    const { component } = useComponentContext();

    const { profiles: distributors } = useProfilesByIds(
        component.distributorsDetails?.map(({ distributor }) => distributor) ?? [],
    );

    return (
        <>
            <GeneralItem label="Available in" icon={<IoGlobeOutline size={14} />}>
                {component.regionAvailability.join(', ')}
            </GeneralItem>

            {!!distributors?.length && (
                <BadgeGroup label="Distributed by" withDivider={false}>
                    <Group gap={6}>
                        {distributors.map((distributor) => (
                            <BadgePill key={distributor.id} fz="sm" fw={600}>
                                <IoBagHandleOutline size={14} />
                                <GeneralDistributorLink distributor={distributor} />
                            </BadgePill>
                        ))}
                    </Group>
                </BadgeGroup>
            )}
        </>
    );
};

const GeneralItem: FC<{ label: string; icon: React.ReactNode; children: React.ReactNode }> = ({
    label,
    icon,
    children,
}) => {
    if (!children || (Array.isArray(children) && children.length === 0)) return null;

    return (
        <BadgeGroup label={label}>
            <BadgePill fw={600}>
                {icon}
                {children}
            </BadgePill>
        </BadgeGroup>
    );
};

const GeneralDistributorLink: FC<{ distributor: CompanyProfile }> = ({ distributor }) => {
    if (distributor.status === PublishedStatus.PUBLISHED) {
        return (
            <Anchor inherit component={Link} href={CompanyHelpers.urls.view(distributor.slug)}>
                {distributor.name}
            </Anchor>
        );
    }

    return (
        <Text inherit span>
            {distributor.name}
        </Text>
    );
};

export { GeneralView };
